import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { ButtonTip } from "@/components/ui/button-tip";
import { EASE, usePrefersReducedMotion } from "./calm";
import { numberedCount, type StepDef } from "./steps";

/**
 * A thin bar that moves softly, the step dots (a dot can be clicked once its step has been visited), and
 * "Schritt 3 von 8" - which a polite live region also says when the step changes.
 */
export default function ProgressHeader({
  steps,
  current,
  maxReached,
  complete,
  onGo,
  titleOf,
}: {
  steps: StepDef[];
  current: StepDef;
  maxReached: number;
  complete: (step: StepDef) => boolean;
  onGo: (step: StepDef) => void;
  titleOf: (step: StepDef) => string;
}) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const scroller = useRef<HTMLOListElement>(null);
  const activeItem = useRef<HTMLLIElement>(null);
  // soft edges only where the row runs on beyond the window
  const [edges, setEdges] = useState({ left: false, right: false });
  const numbered = steps.filter((step) => step.index >= 1 && step.index <= numberedCount(steps));
  const total = numbered.length;
  const position = current.kind === "welcome" ? 0 : Math.min(current.index, total);
  const label = position === 0 ? "" : t("onboardingPage.nav.stepOf", { current: position, total });
  const measure = () => {
    const node = scroller.current;
    if (!node) return;
    const left = node.scrollLeft > 2;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 2;
    setEdges((previous) => (previous.left === left && previous.right === right ? previous : { left, right }));
  };
  // the active circle is brought to the middle of the row (no vertical jump; no animation under reduced motion)
  useEffect(() => {
    activeItem.current?.scrollIntoView?.({ inline: "center", block: "nearest", behavior: reduced ? "auto" : "smooth" });
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.index, total, reduced]);
  useEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined" || !scroller.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(scroller.current);
    return () => observer.disconnect();
  }, []);

  return (
    <nav aria-label={t("onboardingPage.nav.stepsNav")} className="mx-auto w-full max-w-[1120px] px-4" data-testid="progress-nav">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={position}
        className="h-0.5 overflow-hidden rounded-full bg-border"
      >
        <div className="h-full w-full origin-left bg-primary" style={{ transform: `scaleX(${position / Math.max(total, 1)})`, transition: `transform 600ms ${EASE}` }} />
      </div>
      <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
        {/*
          The circles scroll sideways when there are many. The row has room all around (padding, undone by the negative
          margin, so nothing moves): an `overflow` box clips what sticks out of it, and a ring, a focus ring (2 px + 2 px
          offset) and the grown current circle do stick out. Fixed size, no shrinking, snapping; no scroll bar.
        */}
        <div className="relative min-w-0 sm:flex-1">
          <ol
            ref={scroller}
            onScroll={measure}
            className="-m-1.5 flex snap-x snap-mandatory items-center gap-1.5 overflow-x-auto overflow-y-hidden p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="progress-dots"
          >
          {numbered.map((step) => {
            const visited = step.index <= maxReached;
            const isCurrent = step.index === current.index;
            const done = complete(step) && step.index < current.index + 99 && visited && !isCurrent;
            return (
              <li key={step.slug} ref={isCurrent ? activeItem : undefined} className="flex shrink-0 snap-center items-center" data-step={step.slug} data-current={isCurrent ? "true" : "false"} data-done={done ? "true" : "false"}>
                <ButtonTip label={visited ? t("onboardingPage.tips.step", { n: step.index, title: titleOf(step) }) : ""} side="bottom">
                <button
                  type="button"
                  disabled={!visited}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-label={t("onboardingPage.nav.goToStep", { n: step.index, title: titleOf(step) })}
                  onClick={() => onGo(step)}
                  className={cn(
                    "relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold leading-none tabular-nums transition-[background-color,border-color,color,transform] duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    done ? "border-primary bg-primary text-primary-foreground" : isCurrent ? "scale-110 border-primary text-foreground" : "border-border text-muted-foreground",
                    !visited && "cursor-default opacity-60",
                  )}
                >
                  <Check className={cn("absolute h-3 w-3 transition-[opacity,transform] duration-500", done ? "scale-100 opacity-100" : "scale-50 opacity-0")} aria-hidden="true" />
                  <span className={cn("transition-opacity duration-300", done ? "opacity-0" : "opacity-100")}>{step.index}</span>
                </button>
                </ButtonTip>
              </li>
            );
          })}
          </ol>
          {edges.left && <span aria-hidden="true" data-testid="edge-left" className="pointer-events-none absolute inset-y-0 -left-1.5 w-6 bg-gradient-to-r from-background to-transparent" />}
          {edges.right && <span aria-hidden="true" data-testid="edge-right" className="pointer-events-none absolute inset-y-0 -right-1.5 w-6 bg-gradient-to-l from-background to-transparent" />}
        </div>
        <p className="text-xs text-muted-foreground sm:shrink-0 [overflow-wrap:anywhere]" aria-live="polite" data-testid="step-announce">
          {position === 0 ? "" : t("onboardingPage.nav.announce", { current: position, total, title: titleOf(current) })}
          <span aria-hidden="true" className="sr-only">{label}</span>
        </p>
      </div>
    </nav>
  );
}
